// @flow

/* eslint-disable import/no-unresolved */
import './Map.css'
import mapboxgl from 'mapboxgl'
import geoViewport from 'geoViewport'

export default class Map {
  constructor (mapEl, chapters: Array<any>) {
    this.mapReady = false
    this.addedPoints = []
    this.startTime = 0
    this.progress = 0
    this.framesPerSecond = 15
    this.initialOpacity = 1
    this.opacity = this.initialOpacity
    this.initialRadius = 8
    this.radius = this.initialRadius
    this.maxRadius = 18
    this.firstSymbolId = null
    this.activeIndex = 0
    this.chapters = chapters
    this.lineNames = []
    this.mapEl = mapEl

    this.adaptChapterConfigBounds(this.chapters[0])

    mapboxgl.accessToken = 'pk.eyJ1IjoibnJrIiwiYSI6Ii14T3NfSzgifQ.0N5h3SI2b6bemxP_1YESIA'
    this.map = new mapboxgl.Map({
      ...this.chapters[this.activeIndex],
      container: mapEl,
      // style: 'mapbox://styles/nrk/cjda0mdyv8mwk2spdhnfk8upp',
      style: 'mapbox://styles/nrk/cjd5nkoti4kph2spd13z34tmd',
      // style: 'mapbox://styles/nrk/cj07whxya001b2rr3t3qn875e',
      interactive: false
    })

    this.map.on('load', () => {
      const layers = this.map.getStyle().layers
      // Find the index of the first symbol layer in the map style
      this.firstSymbolId = layers.find(layer => layer.type === 'symbol').id
      // layers.find(layer => layer.type === 'symbol').id

      this.map.addControl(new mapboxgl.ScaleControl())

      this.animateMarker(0)

      this.mapReady = true

      this.setActiveChapter(this.activeIndex, 0)
    })
  }

  adaptChapterConfigBounds (chapterConfig) {
    const mapEl = this.mapEl.getBoundingClientRect()
    const bounds = [].concat.apply([], (chapterConfig.bounds))
    const mapDim = [mapEl.width, mapEl.height]
    const newBounds = geoViewport.viewport(bounds, mapDim, 0, 20, 512)

    chapterConfig.zoom = newBounds.zoom
    chapterConfig.center = newBounds.center
  }

  addChapter (chapter) {
    if (chapter.point) {
      for (let i = 0; i < this.addedPoints.length; i++) {
        if (this.map.getLayer(this.addedPoints[i]) !== undefined) {
          this.map.setPaintProperty(this.addedPoints[i], 'circle-opacity', 0)
        }
      }

      this.addPoint(chapter.point, chapter.id)
    }

    if (chapter.line) {
      this.addLine(chapter.line, chapter.id)
    }
  }

  removeChapter (chapter) {
    if (chapter.point) {
      this.removePoint(chapter.id)
    }

    if (chapter.line) {
      this.removeLine(chapter.id)
    }
  }

  setActiveChapter (chapterIndex: number, duration) {
    this.activeIndex = chapterIndex
    if (!this.mapReady) {
      return
    }

    this.chapters.forEach((chapter, idx) => {
      if (idx <= chapterIndex) {
        this.addChapter(chapter)
      } else {
        this.removeChapter(chapter)
      }
    })

    const activeChapter = this.chapters[chapterIndex]
    if (activeChapter.bounds) {
      this.adaptChapterConfigBounds(activeChapter)
    }

    if (duration === 0) {
      this.map.jumpTo(activeChapter)
    } else {
      this.map.flyTo(activeChapter)
    }
  }

  animateMarker (timestamp) {
    setTimeout(() => {
      requestAnimationFrame(this.animateMarker.bind(this))

      this.radius += (this.maxRadius - this.radius) / this.framesPerSecond
      this.opacity -= (0.9 / this.framesPerSecond)

      if (this.opacity <= 0) {
        this.radius = this.initialRadius
        this.opacity = this.initialOpacity
      }

      const currentPointName = `point-${this.chapters[this.activeIndex].id}`
      if (this.map.getLayer(currentPointName) !== undefined) {
        this.map.setPaintProperty(currentPointName, 'circle-radius', this.radius)
        this.map.setPaintProperty(currentPointName, 'circle-opacity', this.opacity)
      }
    }, 1000 / this.framesPerSecond)
  }

  addLine (line, id) {
    const lineId = `line-${id}`
    if (this.lineNames.indexOf(lineId) === -1) {
      this.map.addLayer({
        'id': lineId,
        'type': 'line',
        'source': {
          'type': 'geojson',
          'data': line.data
        },
        'layout': {
          'line-cap': 'round',
          'line-join': 'round'
        },
        'paint': {
          'line-color': '#bb1414',
          // 'line-dasharray': [5, 5],
          'line-width': 5,
          'line-opacity': 0.6
        }
      }, this.firstSymbolId)

      this.lineNames.push(lineId)
    }
  }

  removeLine (id) {
    const lineId = `line-${id}`
    if (this.map.getLayer(lineId) !== undefined) {
      this.map.removeLayer(lineId)
      this.map.removeSource(lineId)

      this.lineNames.splice(this.lineNames.indexOf(lineId), 1)
    }
  }

  removePoint (id) {
    const pointId = `point-${id}`
    if (this.map.getLayer(pointId) !== undefined) {
      this.map.removeLayer(pointId)
      this.map.removeLayer(pointId + '_base')
      this.map.removeSource(pointId)

      this.addedPoints.splice(this.addedPoints.indexOf(pointId), 1)
    }
  }

  addPoint (point, id) {
    const pointId = `point-${id}`
    if (this.addedPoints.indexOf(pointId) === -1) {
      this.map.addSource(pointId, {
        'type': 'geojson',
        'data': {
          'type': 'Point',
          'coordinates': [
            point.lon, point.lat
          ]
        }
      })

      this.map.addLayer({
        'id': pointId,
        'source': pointId,
        'type': 'circle',
        'paint': {
          'circle-radius': this.initialRadius,
          'circle-radius-transition': { duration: 0 },
          'circle-opacity-transition': { duration: 0 },
          'circle-color': '#dd0000'
        }
      })

      this.map.addLayer({
        'id': pointId + '_base',
        'source': pointId,
        'type': 'circle',
        'paint': {
          'circle-radius': 5,
          'circle-color': '#000000',
          'circle-opacity': 0.5
        }
      })

      this.addedPoints.push(pointId)
    }
  }
}



// WEBPACK FOOTER //
// ./src/v1/components/Map.js